import { CHARACTERS } from '../data/characters.js';
import { audio } from '../systems/audio/AudioSystem.js';
import { ASCENSION_ICON, ASCENSIONS, getAscensionStatMultiplier } from '../data/ascensions.js';
import { PLAYER_SPRITES } from '../data/sprites.js';
import { drawSprite } from '../core/Sprite.js';

// Pixel portrait for a champion card — backing canvas at the sprite's natural
// (tiny) resolution, upscaled by CSS with image-rendering: pixelated, same
// approach as the pet gallery (ShopSummonMenu.js) but sized from the sprite
// so no grid change can clip it.
function buildPortrait(character) {
  const sprite = PLAYER_SPRITES[character.id];
  const canvas = document.createElement('canvas');
  canvas.className = 'character-portrait';
  const w = Math.ceil((sprite.rows[0]?.length ?? 0) * sprite.pixelSize);
  const h = Math.ceil(sprite.rows.length * sprite.pixelSize);
  const size = Math.max(w, h) + 2;
  canvas.width = size;
  canvas.height = size;
  drawSprite(canvas.getContext('2d'), sprite, size / 2, size / 2);
  return canvas;
}

/** Character-pick screen between the main menu and the run starting. */
export class CharacterSelect {
  constructor(root, save, { onPick, onBack }) {
    this.root = root;
    this.save = save;
    this.onPick = onPick;
    this.onBack = onBack;
    this.ascension = 0;
    // Per-run opt-out of permanent progression — see Game.js's constructor
    // (disableEchoUpgrades/disableShardPerks/disablePetBonuses). Purely a
    // "how this one run reads the save" toggle; nothing is spent or lost.
    // Seeded from — and persisted straight back to — the save (see the
    // checkbox handlers below) so a choice made for one run (e.g. a fair
    // challenge run) carries over to the next instead of silently resetting
    // every time this screen is reopened.
    this.disableEchoUpgrades = save.disableEchoUpgrades;
    this.disableShardPerks = save.disableShardPerks;
    this.disablePetBonuses = save.disablePetBonuses;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay character-select-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    this.el.innerHTML = '';

    const header = document.createElement('header');
    header.className = 'character-select-header';
    header.innerHTML = '<div class="menu-title">CHOOSE YOUR CHAMPION</div>';
    this.el.appendChild(header);

    const shards = document.createElement('div');
    shards.className = 'menu-echoes';
    shards.innerHTML = `Shards: <span class="menu-shards-value">${this.save.shards}</span>`;
    this.el.appendChild(shards);

    const mode = document.createElement('div');
    mode.className = 'character-mode-panel';
    mode.innerHTML = `
        <label class="character-ascension-select">Ascension
        <select data-ascension>${ASCENSIONS.map((a) => `<option value="${a.level}">${a.level ? `${ASCENSION_ICON} Ascension ${a.level}` : 'Normal'}</option>`).join('')}</select>
        </label>
        <label class="character-ascension-select" title="On-screen joystick + Dash/Ultimate/Pause buttons, and the no-crop mobile display scaling. Auto follows device detection.">Mobile Controls
        <select data-mobile-controls>
          <option value="auto">Auto</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
        </label>
        <div class="character-ascension-description" data-ascension-description></div>
    `;
    this.el.appendChild(mode);
    const ascensionSelect = mode.querySelector('[data-ascension]');
    const ascensionDescription = mode.querySelector('[data-ascension-description]');
    const updateAscensionDescription = () => {
      const flavor = ASCENSIONS[this.ascension].description;
      const statBoost = Math.round((getAscensionStatMultiplier(this.ascension) - 1) * 100);
      ascensionDescription.textContent = statBoost > 0
        ? `${flavor} · Enemies have +${statBoost}% HP & damage.`
        : flavor;
    };
    ascensionSelect.value = String(this.ascension);
    updateAscensionDescription();
    ascensionSelect.addEventListener('change', (e) => {
      this.ascension = Number(e.target.value);
      updateAscensionDescription();
    });

    const mobileControlsSelect = mode.querySelector('[data-mobile-controls]');
    mobileControlsSelect.value = this.save.mobileControls;
    mobileControlsSelect.addEventListener('change', (e) => {
      this.save.setMobileControls(e.target.value);
    });

    // Challenge-run opt-outs, shown only once there is something to opt out
    // of (see SaveManager.hasPermanentProgression). On a fresh save these are
    // three checkboxes that provably change nothing, parked above the roster
    // on the first screen a new player reads — and they cost exactly the
    // vertical band the roster's second row needs at 720p. Hiding them is
    // both the onboarding fix and the layout fix.
    if (this.save.hasPermanentProgression) {
      const toggles = document.createElement('div');
      toggles.className = 'character-bonus-toggles';
      toggles.innerHTML = `
          <label class="character-bonus-toggle"><input type="checkbox" data-disable-echo> Disable Echo Upgrades</label>
          <label class="character-bonus-toggle"><input type="checkbox" data-disable-shard> Disable Shard Perks</label>
          <label class="character-bonus-toggle"><input type="checkbox" data-disable-pets> Disable Pet Bonuses</label>
      `;
      this.el.appendChild(toggles);
      const echoToggle = toggles.querySelector('[data-disable-echo]');
      const shardToggle = toggles.querySelector('[data-disable-shard]');
      const petsToggle = toggles.querySelector('[data-disable-pets]');
      // _render() re-runs after a locked-character unlock purchase (see
      // _buildLockedCard below), which would otherwise silently drop these
      // back to unchecked — re-sync from state the same way ascensionSelect does above.
      echoToggle.checked = this.disableEchoUpgrades;
      shardToggle.checked = this.disableShardPerks;
      petsToggle.checked = this.disablePetBonuses;
      echoToggle.addEventListener('change', (e) => {
        this.disableEchoUpgrades = e.target.checked;
        this.save.setDisableEchoUpgrades(e.target.checked);
      });
      shardToggle.addEventListener('change', (e) => {
        this.disableShardPerks = e.target.checked;
        this.save.setDisableShardPerks(e.target.checked);
      });
      petsToggle.addEventListener('change', (e) => {
        this.disablePetBonuses = e.target.checked;
        this.save.setDisablePetBonuses(e.target.checked);
      });
    }

    const cards = document.createElement('div');
    cards.className = 'character-cards';
    let firstUnlockedCard = null;
    for (const character of CHARACTERS) {
      const unlocked = this.save.isCharacterUnlocked(character);
      const card = unlocked ? this._buildUnlockedCard(character) : this._buildLockedCard(character);
      if (unlocked && !firstUnlockedCard) firstUnlockedCard = card;
      cards.appendChild(card);
    }
    // Tell ui/menuKeyNav.js where the highlight starts. Without it, its
    // fallback rule ("first .menu-button") lands on Back — the last element
    // on the screen — so arriving here and pressing Enter, which is what
    // navigating any other menu by keyboard teaches you to do, backed out of
    // champion select instead of starting a run. See the autoSelect comment
    // in menuKeyNav.js.
    firstUnlockedCard?.setAttribute('data-menu-default', '');
    this.el.appendChild(cards);

    this._detail = this._buildDetailPanel();
    this.el.appendChild(this._detail);
    this._watchSelection(cards);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  /**
   * The card face: portrait, name, title, stat line. Deliberately compact.
   *
   * The cards used to carry the signature and ultimate paragraphs inline,
   * which put ten multi-paragraph stat-blocks on screen at once. With ten
   * champions in a five-wide grid that needs two rows, and two rows of that
   * height do not fit a 720p viewport — so half the roster sat below an
   * unmarked scroll while the visible row's ultimate text clipped
   * mid-sentence. A new player's first sight of the game was a wall of
   * truncated text hiding five champions they had no way to know existed.
   *
   * The detail those paragraphs carry is real and worth reading, so it moved
   * rather than went away: `_buildDetailPanel` shows it for the highlighted
   * champion (see `_watchSelection`). Compare-at-a-glance on the grid, read
   * in depth on the one you're considering — which is the order a player
   * actually does it in.
   */
  _appendCardBody(card, character, { locked = false } = {}) {
    const label = document.createElement('div');
    label.className = 'levelup-card-label';
    label.textContent = character.name;
    card.appendChild(label);

    if (character.title) {
      const title = document.createElement('div');
      title.className = 'character-card-title';
      title.textContent = character.title;
      card.appendChild(title);
    }

    // A locked card spends this row on its Unlock button instead of the stat
    // line, so both card types are exactly four elements tall and the two
    // grid rows keep one height. They did not, briefly, and the extra row on
    // five of ten cards pushed the Unlock buttons under the detail panel —
    // the one control on those cards, clipped. The stat line is not lost:
    // .character-detail carries it, along with the signature and ultimate the
    // player is deciding between.
    if (!locked) {
      const desc = document.createElement('div');
      desc.className = 'levelup-card-desc';
      desc.textContent = character.description;
      card.appendChild(desc);
    }
  }

  /**
   * The read-in-depth half of the screen: signature and ultimate for whichever
   * champion is currently highlighted. Fixed height so that moving the
   * highlight along the row never reflows the grid above it — a panel that
   * resizes under the cursor makes the roster feel like it is squirming.
   */
  _buildDetailPanel() {
    const panel = document.createElement('div');
    panel.className = 'character-detail';
    panel.setAttribute('aria-live', 'polite');
    return panel;
  }

  _renderDetail(character) {
    const panel = this._detail;
    if (!panel) return;
    panel.innerHTML = '';
    if (!character) return;
    panel.style.setProperty('--champ', character.color);

    const heading = document.createElement('div');
    heading.className = 'character-detail-heading';
    heading.textContent = character.title ? `${character.name} — ${character.title}` : character.name;
    panel.appendChild(heading);

    // The stat line. On the grid this sits on the card, but a locked card
    // spends that row on its Unlock button (see _appendCardBody), so the
    // panel is the only place a player can read what a champion they haven't
    // bought actually starts with — which is most of the reason to buy one.
    const stats = document.createElement('div');
    stats.className = 'character-detail-stats';
    stats.textContent = character.description;
    panel.appendChild(stats);

    const traits = document.createElement('div');
    traits.className = 'character-detail-traits';
    if (character.signature) {
      traits.appendChild(this._buildTrait('Signature', character.signature.name, character.signature.description));
    }
    if (character.ultimate) {
      traits.appendChild(this._buildTrait('Ultimate', character.ultimate.name, character.ultimate.description));
    }
    panel.appendChild(traits);

    if (!this.save.isCharacterUnlocked(character)) {
      const locked = document.createElement('div');
      locked.className = 'character-detail-locked';
      locked.textContent = `Locked — ${character.unlockCost} Shards. You have ${this.save.shards}.`;
      panel.appendChild(locked);
    }
  }

  /**
   * Mirror whatever ui/menuKeyNav.js has highlighted into the detail panel.
   *
   * A MutationObserver rather than this screen binding its own hover and key
   * handlers: menuKeyNav already owns the highlight, and it drives it from
   * both mouse and keyboard through one shared selection. Re-deriving that
   * here would mean a second navigator to keep in sync — and the two would
   * disagree the first time either changed. Watching the class it paints is
   * the one way to follow the selection without owning it.
   */
  _watchSelection(cards) {
    this._selectionObserver?.disconnect();
    const sync = () => {
      // closest(), not dataset directly: a locked champion's card is a <div>
      // wrapping the navigable Unlock <button>, so the highlight can land on
      // a child of the card rather than the card itself.
      const el = cards.querySelector('.menu-button-selected') ?? cards.firstElementChild;
      const id = el?.closest('[data-character-id]')?.dataset.characterId;
      this._renderDetail(CHARACTERS.find((c) => c.id === id) ?? null);
    };
    this._selectionObserver = new MutationObserver(sync);
    this._selectionObserver.observe(cards, { attributes: true, attributeFilter: ['class'], subtree: true });
    sync();
  }

  _buildTrait(kind, name, description) {
    const trait = document.createElement('div');
    trait.className = 'character-card-trait';
    const heading = document.createElement('div');
    heading.className = 'character-card-trait-name';
    const kindLabel = document.createElement('span');
    kindLabel.className = 'character-card-trait-kind';
    kindLabel.textContent = kind;
    heading.appendChild(kindLabel);
    heading.append(` ${name}`);
    trait.appendChild(heading);
    const body = document.createElement('div');
    body.className = 'character-card-trait-desc';
    body.textContent = description;
    trait.appendChild(body);
    return trait;
  }

  _buildUnlockedCard(character) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'levelup-card';
    card.dataset.characterId = character.id;
    card.style.setProperty('--champ', character.color);
    card.appendChild(buildPortrait(character));
    this._appendCardBody(card, character);

    card.addEventListener('click', () => {
      audio.playSfx('click');
      this.onPick(character, {
        ascension: this.ascension,
        disableEchoUpgrades: this.disableEchoUpgrades,
        disableShardPerks: this.disableShardPerks,
        disablePetBonuses: this.disablePetBonuses,
      });
    });
    return card;
  }

  _buildLockedCard(character) {
    const canAfford = this.save.shards >= character.unlockCost;

    const card = document.createElement('div');
    card.className = 'levelup-card levelup-card-locked';
    card.dataset.characterId = character.id;
    card.style.setProperty('--champ', character.color);
    card.appendChild(buildPortrait(character));
    this._appendCardBody(card, character, { locked: true });

    const unlockButton = document.createElement('button');
    unlockButton.type = 'button';
    unlockButton.className = 'menu-button menu-button-small';
    unlockButton.textContent = `Unlock — ${character.unlockCost} Shards`;
    unlockButton.disabled = !canAfford;
    unlockButton.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.save.spendShards(character.unlockCost)) {
        this.save.unlockCharacter(character.id);
        audio.playSfx('levelup');
        this._render();
      }
    });
    card.appendChild(unlockButton);

    return card;
  }

  destroy() {
    this._selectionObserver?.disconnect();
    this.el.remove();
  }
}
